import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';
import {
  IconAlert,
  IconBook,
  IconChart,
  IconCheck,
  IconFile,
  IconInfo,
  IconLock,
  IconMessageSquare,
  IconShield,
  IconUser,
  IconZap,
} from '../components/Icons';
import './Security.css';

function Security({ onNavigate }) {
  return (
    <div className="security-page">
      <Helmet>
        <title>Security & Data Protection - RiskLo</title>
        <meta
          name="description"
          content="How RiskLo handles strategy data, sign-in, broker connections, NinjaTrader sync, and data storage. Encrypted transit, encrypted broker credentials, and access enforced on our servers."
        />
        <meta property="og:title" content="Security & Data Protection - RiskLo" />
        <meta
          property="og:description"
          content="RiskLo security: Google sign-in limited to your name and email, encrypted broker credentials, membership-gated strategy data, and clear data boundaries."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://risklo.io/security" />
        <meta property="og:image" content="https://risklo.io/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Security & Data Protection - RiskLo" />
        <meta
          name="twitter:description"
          content="How RiskLo protects your data: sign-in only Google access, encrypted broker credentials, membership-gated strategy data, optional broker and NinjaTrader sync."
        />
      </Helmet>
      <PageHeader onNavigate={onNavigate} />
      <div className="security-hero">
        <div className="security-badge">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z"
              fill="#4CAF50"
            />
            <path d="M10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" fill="white" />
          </svg>
          <h1>Google Verified Application</h1>
          <p className="security-subtitle">
            When you sign in with Google, RiskLo uses Google&apos;s OAuth process and only asks for your name, email
            address, and profile picture. We never see your Google password and never request access to your Gmail,
            Drive, or Sheets.
          </p>
          <div className="verification-links">
            <a
              href="https://support.google.com/cloud/answer/9110914"
              target="_blank"
              rel="noopener noreferrer"
              className="verification-link"
            >
              <IconBook size={18} color="currentColor" />
              Learn about Google&apos;s verification process
            </a>
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="verification-link"
            >
              <IconUser size={18} color="currentColor" />
              Check your connected apps
            </a>
          </div>
        </div>
      </div>

      <div className="security-content">
        <section className="security-section data-clarification-section">
          <div className="data-clarification">
            <div className="clarification-icon-svg" aria-hidden="true">
              <IconChart size={48} color="rgba(129, 140, 248, 0.95)" />
            </div>
            <div className="clarification-content">
              <h2>What data does RiskLo use?</h2>
              <div className="clarification-main">
                <p className="clarification-intro">RiskLo combines several kinds of data, depending on what you use:</p>
                <ol className="clarification-list">
                  <li>
                    <strong>Strategy results from your brand</strong> — Performance data for the strategies your
                    brand publishes on RiskLo. Brands provide it through spreadsheets they share with our read-only
                    service account, CSV uploads, or automated alerts (for example TradingView or NinjaTrader
                    webhooks). We store snapshots for speed, and your membership decides what you can see.
                  </li>
                  <li>
                    <strong>What you enter or upload</strong> — Account size, contracts, drawdowns, CSVs, and similar
                    inputs for blowout risk, % rule, portfolio, and bulk tools.
                  </li>
                  <li>
                    <strong>Optional broker connections (My Portfolio)</strong> — If you connect a broker such as
                    Coinbase, Alpaca, Interactive Brokers, or Tradovate, we store the API keys or tokens you provide,
                    encrypted, and use them to read your balances, positions, and trades.
                  </li>
                  <li>
                    <strong>Optional NinjaTrader sync (RiskLo Connector)</strong> — If you install the RiskLo
                    Connector add-on, the trades, account values, and position alerts it sends are stored to power
                    &quot;My Results&quot;, Trade Watch alerts, and related dashboards.
                  </li>
                </ol>
                <p className="clarification-note">
                  Core calculators do <strong>not</strong> require a broker login. Broker and NinjaTrader connections
                  are optional and only happen when you set them up.
                </p>
              </div>
              <div className="clarification-details">
                <div className="detail-item">
                  <span className="detail-icon detail-icon-yes" aria-hidden="true">
                    <IconCheck size={20} color="#4ade80" />
                  </span>
                  <span>
                    Strategy dashboards use performance stats (e.g. P&amp;L, win rates, drawdowns) supplied by your
                    brand, subject to your membership.
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon detail-icon-yes" aria-hidden="true">
                    <IconCheck size={20} color="#4ade80" />
                  </span>
                  <span>
                    Sign-in can be email/password, Google, Microsoft, or Whop. We never see your Google, Microsoft, or
                    Whop password.
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon detail-icon-no" aria-hidden="true">
                    <IconAlert size={20} color="#f87171" />
                  </span>
                  <span>
                    We do <strong>not</strong> show a brand&apos;s strategy data to users without access to that brand
                    and plan; those areas stay locked, and our servers enforce it.
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon detail-icon-no" aria-hidden="true">
                    <IconAlert size={20} color="#f87171" />
                  </span>
                  <span>
                    We do <strong>not</strong> open, close, or change trades, and we never move money. Broker
                    connections only read balances, positions, and trades. Nobody at RiskLo, including support,
                    can close a position for you. If you run the RiskLo Tally add-on, its auto-close is a setting on
                    your own computer that only you can switch on or off.
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon detail-icon-no" aria-hidden="true">
                    <IconAlert size={20} color="#f87171" />
                  </span>
                  <span>
                    We do <strong>not</strong> store broker API keys or tokens in plain text, and we never receive
                    your Google or Microsoft password.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="security-section">
          <h2>How we protect your data</h2>
          <div className="security-grid">
            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconShield size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Sign-in only Google access</h3>
              <p>
                Google sign-in uses Google&apos;s OAuth and requests only your basic profile (name, email, picture).
                You can revoke it anytime from your Google account.
              </p>
            </div>

            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconLock size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Encrypted broker credentials</h3>
              <p>
                Broker API keys and access tokens are encrypted before they are stored, and saved keys are never sent
                back to your browser in full.
              </p>
            </div>

            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconZap size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Strategy data on the server</h3>
              <p>
                Brand strategy data is read with read-only access and checked on our servers against your brand and
                plan before anything is shown. Data may be cached in our database for performance and reliability.
              </p>
            </div>

            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconFile size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Secret alert links</h3>
              <p>
                Every webhook or alert link carries its own secret token, and anything sent without a valid token is
                rejected. Tokens can be rotated if one is ever exposed.
              </p>
            </div>

            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconInfo size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Secure storage &amp; transit</h3>
              <p>
                Account, session, synced snapshots, and connected broker or NinjaTrader data are stored on protected
                infrastructure, sent over TLS, and handled according to our Privacy Policy.
              </p>
            </div>

            <div className="security-card">
              <div className="security-card-icon-svg" aria-hidden="true">
                <IconCheck size={28} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h3>Passwords &amp; your controls</h3>
              <p>
                Email/password credentials are hashed; social sign-in is handled by the provider. You can disconnect
                brokers or the RiskLo Connector, revoke Google access, and sign out at any time.
              </p>
            </div>
          </div>
        </section>

        <section className="security-section">
          <h2>What data do we access?</h2>
          <div className="data-access-grid">
            <div className="data-access-column">
              <h3 className="access-yes">We may access / store</h3>
              <ul>
                <li>Your name, email, and account settings for sign-in, membership, and support</li>
                <li>Strategy names and performance data your brand provides, for members with access</li>
                <li>Synced copies in our systems for performance, backups, and features you use</li>
                <li>Risk inputs you type or upload (sizes, contracts, CSVs, etc.)</li>
                <li>Balances, positions, and trades from brokers you connect (credentials encrypted)</li>
                <li>Trades, account values, and position alerts sent by the RiskLo Connector, if you install it</li>
              </ul>
            </div>
            <div className="data-access-column">
              <h3 className="access-no">We do not</h3>
              <ul>
                <li>Ask for your broker&apos;s website password</li>
                <li>Open trades, withdraw, or move money in any connected account</li>
                <li>Access your Gmail, Drive, Sheets, or Photos through Google sign-in</li>
                <li>Share your data for unrelated marketing without consent described in our Privacy Policy</li>
                <li>Skip membership rules: brand and plan access is enforced on our servers</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="security-section">
          <h2>How to revoke Google access</h2>
          <div className="revoke-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Go to Google Account Settings</h3>
                <p>
                  Visit{' '}
                  <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
                    myaccount.google.com/permissions
                  </a>
                </p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Find RiskLo</h3>
                <p>Look for &quot;RiskLo&quot; in your list of connected apps</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Remove access</h3>
                <p>Click remove/revoke to end Google permissions for RiskLo</p>
              </div>
            </div>
          </div>
        </section>

        <section className="security-section">
          <h2>Security certifications &amp; practices</h2>
          <div className="cert-badges">
            <div className="cert-badge">
              <div className="cert-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </div>
              <h4>Google OAuth 2.0</h4>
              <p>Used for Sign in with Google</p>
            </div>
            <div className="cert-badge">
              <div className="cert-icon-svg" aria-hidden="true">
                <IconLock size={36} color="rgba(129, 140, 248, 0.95)" />
              </div>
              <h4>TLS encryption</h4>
              <p>Encrypted connections between your browser and RiskLo</p>
            </div>
            <div className="cert-badge">
              <div className="cert-icon-svg" aria-hidden="true">
                <IconCheck size={36} color="#4ade80" />
              </div>
              <h4>Google verified app</h4>
              <p>Verified by Google for sign-in</p>
            </div>
          </div>
        </section>

        <section className="security-section security-faq">
          <h2>Frequently asked questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>Can RiskLo see my Google password?</h3>
              <p>No. Google sign-in uses OAuth; we never receive your Google password.</p>
            </div>
            <div className="faq-item">
              <h3>What happens to strategy data?</h3>
              <p>
                Strategy results your brand provides are processed and stored in our systems to run the product (e.g.
                snapshots, charts, and dashboards). See the Privacy Policy for retention and requests.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can RiskLo change my brand&apos;s spreadsheet or my broker account?</h3>
              <p>
                No. Brand spreadsheets are read with read-only access. Broker connections only read balances,
                positions, and trades. RiskLo never sends an order. The only thing your board can change in
                NinjaTrader is the RiskLo Tally auto-close setting on your own accounts, and only you can change it.
              </p>
            </div>
            <div className="faq-item">
              <h3>Who can see my trading inputs and synced data?</h3>
              <p>
                You, plus RiskLo staff and your brand&apos;s support team when they need to help with your account.
                It is never shown to other members.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I verify the Google integration?</h3>
              <p>
                Check RiskLo under Google Account permissions and review Google&apos;s verification documentation linked
                above.
              </p>
            </div>
          </div>
        </section>

        <section className="security-section security-contact">
          <h2>Still have questions?</h2>
          <p>
            For security or data questions, contact us. For legal terms and privacy details, see Terms &amp; Conditions
            and Privacy Policy in the footer.
          </p>
          <a href="mailto:info@cultivatedynamics.com" className="contact-button">
            <IconMessageSquare size={18} color="currentColor" />
            Contact security
          </a>
        </section>
      </div>
    </div>
  );
}

export default Security;
